/**
 * Global State Reset Utility
 *
 * This utility provides a centralized way to reset all application stores
 * when a user logs out or when their authentication token expires.
 */

import { analytics } from "@/lib/analytics";
import { clearSentryUserContext } from "@/lib/sentry";
import { useConsultationModalStore } from "../consultation";
import { useConsultationStore } from "../consultation/consultationStore";
import { useAdditionalInfo } from "../domains/additionalInfo";
import { useClinicStore } from "../domains/healthcare/clinicStore";
import { usePatientStore } from "../domains/healthcare/patientStore";
import { useVisitStore } from "../domains/healthcare/visitStore";
import { useVitalSignsStore } from "../domains/healthcare/vitalSignsStore";
import { useUserStore } from "../domains/user/userStore";

type ResetAllStoresOptions = {
  preserveAdditionalInfo?: boolean;
};

/**
 * Reset all application stores to their initial state
 * This should be called when a user logs out or when their authentication token expires
 *
 * Use preserveAdditionalInfo for same-user re-login flows so the user does not
 * need to select organization/location again after session expiry.
 */
export const resetAllStores = (options: ResetAllStoresOptions = {}): void => {
  const { preserveAdditionalInfo = false } = options;

  clearSentryUserContext();
  analytics.clearUser();

  // Reset user domain stores
  useUserStore.getState().logout();

  // Reset healthcare domain stores
  useClinicStore.getState().reset();
  usePatientStore.getState().reset();
  useVisitStore.getState().reset();
  useVitalSignsStore.getState().reset();

  // Reset consultation stores
  useConsultationStore.getState().reset();
  useConsultationModalStore.getState().reset();
  // Consultation socket store removed (replaced by useSectionSocket)

  if (!preserveAdditionalInfo) {
    // Reset additional info (organization/location selection)
    // Use this for full account switches or explicit debug resets.
    useAdditionalInfo.getState().reset();
  }

  // Do not reset UI preferences like theme settings
  // useThemeStore.getState().reset();

  // Add any other stores that need to be reset here

  console.log("[Store Reset] All stores have been reset");
};

/**
 * Hook to expose the resetAllStores function
 * This can be used in components that need to reset all stores
 */
export const useResetAllStores = () => {
  return { resetAllStores };
};
